import {
  InfiniteListBase,
  useGetList,
  useShowContext,
  useTranslate,
  type ShowBaseProps,
} from "ra-core";
import { AppShowBase } from "@/components/admin/record-load";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { MISSING_DATE_LABEL, toValidDate } from "@/lib/safeDate";
import { cn } from "@/lib/utils";

import { NoteCreate, NotesIterator } from "../notes";
import { nullsLastMeta } from "../providers/commons/nullsLast";
import { openDesktopNote } from "../notes/noteFieldFocus";
import { StatusBadge, contactName } from "../palmtree/badges";
import { contactEditPath, contactTypeLabel } from "../palmtree/constants";
import {
  DUE_LABEL_KEYS,
  dueBucket,
  dueBucketMadrid,
  dutchRelativeDay,
  dutchRelativeDayMadrid,
  madridClock,
} from "../palmtree/dates";
import { NextActionButtons } from "../palmtree/NextActionButtons";
import { PlanActionButton } from "../palmtree/PlanActionButton";
import { panelClass, rowClass, sectionLabelClass } from "../palmtree/shell";
import type { Contact, Task } from "../types";

export const ContactShow = (props: ShowBaseProps = {}) => (
  <AppShowBase {...props}>
    <ContactKaart />
  </AppShowBase>
);

const ContactKaart = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Contact>();
  const { data: actions = [], isPending: actionsPending } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "due_date", order: "ASC" },
      filter: record
        ? { contact_id: record.id, "done_date@is": null }
        : undefined,
      meta: nullsLastMeta,
    },
    { enabled: !!record },
  );

  if (isPending || !record) return null;

  const nextAction = actions[0];
  const email = record.email_jsonb?.[0]?.email;
  const phone = record.phone_jsonb?.[0]?.number;
  const timed = nextAction?.due_has_time === true;
  const due = nextAction ? toValidDate(nextAction.due_date) : null;
  const when = !nextAction
    ? ""
    : !due
      ? MISSING_DATE_LABEL
      : timed
        ? `${dutchRelativeDayMadrid(nextAction.due_date)} om ${madridClock(nextAction.due_date)}`
        : dutchRelativeDay(nextAction.due_date);
  const nextBucket = !due
    ? null
    : timed
      ? dueBucketMadrid(nextAction.due_date)
      : dueBucket(nextAction.due_date);

  return (
    <div className={`${panelClass} space-y-4 p-4 md:p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-[22px] font-bold tracking-tight">
            {contactName(record)}
          </h2>
          <div className="mt-2 flex flex-wrap gap-2">
            <StatusBadge status={record.status} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to={contactEditPath(record.id)}>
              {translate("palmtree.contact.edit")}
            </Link>
          </Button>
          {/* A link to `#timeline` would replace ra-core's `#/contacten/:id` fragment. */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={openDesktopNote}
          >
            {translate("palmtree.contact.note")}
          </Button>
          <PlanActionButton contactId={record.id} variant="default" />
        </div>
      </div>

      <section className="rounded-lg border border-next-action-border bg-gradient-to-b from-next-action-from to-next-action-to p-4">
        <h3 className={`${sectionLabelClass} text-link`}>
          {translate("palmtree.contact.next_action")}
        </h3>
        {actionsPending ? null : nextAction ? (
          <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-base font-bold">{nextAction.text}</p>
              <p
                className={cn(
                  "mt-1 text-[12.5px]",
                  nextBucket === "overdue"
                    ? "text-warn"
                    : "text-muted-foreground",
                )}
              >
                {nextBucket
                  ? translate("palmtree.contact.due_line", {
                      when,
                      bucket: translate(DUE_LABEL_KEYS[nextBucket]),
                    })
                  : when}
              </p>
            </div>
            <NextActionButtons task={nextAction} />
          </div>
        ) : (
          <p className="mt-3 text-[12.5px] text-muted-foreground">
            {translate("palmtree.contact.no_open_action")}
          </p>
        )}
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
        <Field
          label={translate("palmtree.contact.field_type")}
          value={contactTypeLabel(record.type)}
        />
        <Field
          label={translate("palmtree.contact.field_company")}
          value={record.company_name}
        />
        <Field
          label={translate("palmtree.contact.field_role")}
          value={record.title}
        />
        <Field
          label={translate("palmtree.contact.field_email")}
          value={email}
        />
        <Field
          label={translate("palmtree.contact.field_phone")}
          value={phone}
        />
        <Field
          label={translate("palmtree.contact.field_channel")}
          value={record.preferred_channel}
        />
      </section>

      <section id="timeline">
        <h3 className="mb-3 text-xl font-bold tracking-tight">
          {translate("palmtree.contact.timeline")}
        </h3>
        <InfiniteListBase
          resource="contact_notes"
          filter={{ contact_id: record.id }}
          sort={{ field: "date", order: "DESC" }}
          perPage={25}
          queryOptions={{ meta: nullsLastMeta }}
          disableSyncWithLocation
          storeKey={false}
          empty={
            <NoteCreate
              reference="contacts"
              showStatus={false}
              className="mt-2 rounded-row border border-dashed border-line-strong p-3"
            />
          }
        >
          <NotesIterator reference="contacts" showStatus={false} />
        </InfiniteListBase>
      </section>
    </div>
  );
};

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div className={rowClass}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="mt-0.5">{value || "—"}</div>
  </div>
);
